import { updateAttribute } from "@/utils/attribute"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"
import { toast } from "sonner"
import {
  type CatalogLanguage,
  getCatalog,
  loadCatalog,
  type TranslationKeys,
} from "./catalogs"
import { formatTranslation } from "./formatTranslation"

export type { TranslationKeys } from "./catalogs"

interface LanguageRecord {
  key: string
  name: string
}

// JA is temporarily hidden from language selection (no Japanese customers yet);
// translations_JA.ts and its loader in catalogs.ts are kept so it can be
// re-enabled by adding the entry back here.
export const Languages = {
  EN: { key: "EN", name: "English" },
  DE: { key: "DE", name: "Deutsch" },
} as Record<string, LanguageRecord>

export type LanguageKeys = keyof typeof Languages
export const languageOptions: LanguageRecord[] = Object.values(Languages)

/**
 * How a language switch ended: `saved` — switched and stored on the account
 * (which the notification emails read); `switched` — switched here, but the
 * account kept the old language; `unchanged` — no switch (the same language,
 * or its catalog failed to load, which toasts).
 */
export type LanguageChangeResult = "saved" | "switched" | "unchanged"

interface TranslationContextProps {
  translate: (
    key: TranslationKeys,
    dynamicValues?: Record<string, string | number>,
  ) => string
  /** `onSettled` hears how the switch ended, once it was saved or not. */
  changeLanguage: (
    language: LanguageKeys,
    onSettled?: (result: LanguageChangeResult) => void,
  ) => void
  currentLanguage: LanguageKeys
  currentLanguageName: string
}

const TranslationContext = createContext<TranslationContextProps | undefined>(
  undefined,
)

TranslationContext.displayName = "TranslationContext"

export const getDefaultLanguage = (): LanguageKeys => {
  const savedLanguage = localStorage.getItem("language") as LanguageKeys | null

  // If a language is saved in localStorage, use it
  if (savedLanguage && savedLanguage in Languages) {
    return savedLanguage
  }

  // Otherwise, detect from browser
  const browserLanguage = navigator.language.split("-")[0].toUpperCase() // e.g., "de" -> "DE"
  return browserLanguage in Languages ? (browserLanguage as LanguageKeys) : "DE"
}

export const TranslationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // React Compiler opt-in (vite.config.mts runs it in annotation mode): the
  // context value is memoized, so consumers re-render only on real changes.
  "use memo"
  const [currentLanguage, setCurrentLanguage] =
    useState<LanguageKeys>(getDefaultLanguage)

  const [currentLanguageName, setCurrentLanguageName] =
    useState<LanguageKeys>("")

  useEffect(() => {
    localStorage.setItem("language", currentLanguage)
    // Screen readers pick their voice from <html lang>, which index.html
    // fixes at "en": keep it on the UI language, on load and on every switch.
    document.documentElement.lang = currentLanguage.toLowerCase()
    setCurrentLanguageName(Languages[currentLanguage].name)
  }, [currentLanguage])

  const translate = useCallback(
    (
      identifier: TranslationKeys,
      dynamicValues?: Record<string, string | number>,
    ): string => {
      const translation =
        getCatalog(currentLanguage as CatalogLanguage)?.[identifier] ||
        identifier
      // The ICU subset lives in `formatTranslation` — shared with moduleTranslate,
      // so a message resolves the same way inside and outside the React tree.
      return formatTranslation(translation, dynamicValues)
    },
    [currentLanguage],
  )

  // The latest language asked for. A switch can wait on its catalog, so a
  // later switch may land first; only the latest request is applied.
  const requestedLanguage = useRef<LanguageKeys | null>(currentLanguage)

  const changeLanguage = useCallback(
    (
      language: LanguageKeys,
      onSettled?: (result: LanguageChangeResult) => void,
    ) => {
      if (!Object.keys(Languages).includes(language))
        return onSettled?.("unchanged")
      if (language === requestedLanguage.current)
        return onSettled?.("unchanged")
      requestedLanguage.current = language
      const apply = () => {
        setCurrentLanguage(language)
        // Explicit user choice → persist to Cognito, once the switch has taken
        // effect (a failed catalog load leaves the stored preference as is).
        // An unauthenticated context (login screen selector) rejects, which
        // is fine: the switch itself stands.
        updateAttribute("custom:language", language).then(
          () => onSettled?.("saved"),
          (error) => {
            console.warn(
              "Could not persist language preference to Cognito",
              error,
            )
            onSettled?.("switched")
          },
        )
      }
      // Switch once the catalog is in, so the UI never renders raw keys. An
      // already-loaded catalog (a repeat switch, the test setup) switches now.
      if (getCatalog(language as CatalogLanguage)) {
        apply()
        return
      }
      loadCatalog(language as CatalogLanguage).then(
        () => {
          if (requestedLanguage.current === language) apply()
          else onSettled?.("unchanged")
        },
        (error) => {
          // Let the user retry the same language.
          if (requestedLanguage.current === language) {
            requestedLanguage.current = null
          }
          console.warn(`Could not load the ${language} translations`, error)
          toast.error(translate("SIDEBAR/LANGUAGE_LOAD_ERROR"))
          onSettled?.("unchanged")
        },
      )
    },
    [translate],
  )

  return (
    <TranslationContext.Provider
      value={{
        translate,
        changeLanguage,
        currentLanguage,
        currentLanguageName,
      }}
    >
      {children}
    </TranslationContext.Provider>
  )
}

export const useTranslation = () => {
  const context = useContext(TranslationContext)
  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider")
  }
  return context
}
